import type { ColormapEntry } from "../hooks/useRasterColormap";

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  const part = (v: number) =>
    Math.round(Math.min(255, Math.max(0, v)))
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return `#${part(r)}${part(g)}${part(b)}`;
}

/**
 * Cor e opacidade do gradiente no valor pedido. Os cortes da faixa quase nunca
 * caem sobre uma parada do colormap, então a borda do recorte precisa dos
 * valores interpolados — usar os da parada vizinha criaria um degrau visível
 * que o raster original não tem. A opacidade acompanha a cor porque um
 * colormap pode ter paradas transparentes (no-data, máscaras) que o recorte
 * não deve tornar visíveis.
 */
export function sampleAtValue(
  entries: ColormapEntry[],
  value: number
): { color: string; opacity: number } {
  const at = ({ color, opacity }: ColormapEntry) => ({ color, opacity });
  if (value <= entries[0].quantity) return at(entries[0]);
  const last = entries[entries.length - 1];
  if (value >= last.quantity) return at(last);

  for (let i = 0; i < entries.length - 1; i++) {
    const a = entries[i];
    const b = entries[i + 1];
    if (value >= a.quantity && value <= b.quantity) {
      const span = b.quantity - a.quantity;
      const t = span === 0 ? 0 : (value - a.quantity) / span;
      const ca = hexToRgb(a.color);
      const cb = hexToRgb(b.color);
      return {
        color: rgbToHex([
          ca[0] + (cb[0] - ca[0]) * t,
          ca[1] + (cb[1] - ca[1]) * t,
          ca[2] + (cb[2] - ca[2]) * t,
        ]),
        opacity: a.opacity + (b.opacity - a.opacity) * t,
      };
    }
  }
  return at(last);
}

export function colorAtValue(entries: ColormapEntry[], value: number): string {
  return sampleAtValue(entries, value).color;
}

/**
 * SLD que exibe apenas os pixels dentro de [minVal, maxVal], mantendo as cores
 * do colormap original do raster. Fora da faixa as paradas ficam com
 * opacity="0" — num ColorMap "ramp" o GeoServer interpola também a opacidade,
 * então o corte precisa de uma parada opaca no limite e outra transparente
 * imediatamente ao lado, senão o recorte desbotaria em vez de cortar.
 */
export function buildRangeSldBody({
  workspace,
  layerName,
  entries,
  minVal,
  maxVal,
  opacity = 1,
}: {
  workspace: string;
  layerName: string;
  entries: ColormapEntry[];
  minVal?: number;
  maxVal?: number;
  opacity?: number;
}): string {
  const first = entries[0].quantity;
  const last = entries[entries.length - 1].quantity;
  // Distância entre a parada opaca e a transparente do corte: pequena o
  // bastante para o olho ler como borda dura, grande o bastante para não
  // sumir na precisão do float do GeoServer.
  const eps = Math.max((last - first) / 10_000, 1e-6);

  const cuts: Array<{ quantity: number; color: string; opacity: number }> = [];

  // O corte só precisa criar a parada opaca quando não cai sobre uma parada do
  // colormap; se cair, a própria parada original já é a borda — duplicá-la
  // repetiria a mesma quantity no ColorMap.
  const hasEntryAt = (value: number) =>
    entries.some((entry) => entry.quantity === value);

  if (minVal !== undefined && minVal > first) {
    const sample = sampleAtValue(entries, minVal);
    cuts.push({ quantity: minVal - eps, color: sample.color, opacity: 0 });
    if (!hasEntryAt(minVal)) {
      cuts.push({
        quantity: minVal,
        color: sample.color,
        opacity: sample.opacity,
      });
    }
  }

  // Comparação estrita: a parada exatamente no limite pertence à faixa. Com
  // `<=`/`>=` ela sumia, e quando o limite coincidia com a borda do colormap
  // (o padrão do "Entre", que começa no máximo do slider) nenhum dos blocos de
  // corte a repunha — o raster perdia a cor da ponta.
  for (const entry of entries) {
    if (minVal !== undefined && entry.quantity < minVal) continue;
    if (maxVal !== undefined && entry.quantity > maxVal) continue;
    cuts.push({
      quantity: entry.quantity,
      color: entry.color,
      opacity: entry.opacity,
    });
  }

  if (maxVal !== undefined && maxVal < last) {
    const sample = sampleAtValue(entries, maxVal);
    // `minVal === maxVal`: o bloco do min já criou a parada opaca nesse
    // valor. Repeti-la duplicaria a mesma quantity no ColorMap, o que o
    // GeoServer rejeita.
    if (!hasEntryAt(maxVal) && maxVal !== minVal) {
      cuts.push({
        quantity: maxVal,
        color: sample.color,
        opacity: sample.opacity,
      });
    }
    cuts.push({ quantity: maxVal + eps, color: sample.color, opacity: 0 });
  }

  const colorMapEntries = cuts
    .map(
      (c) =>
        `<ColorMapEntry color="${c.color}" quantity="${c.quantity}" opacity="${c.opacity}"/>`
    )
    .join("");

  return (
    `<StyledLayerDescriptor version="1.0.0" xmlns="http://www.opengis.net/sld">` +
    `<NamedLayer><Name>${workspace}:${layerName}</Name><UserStyle><FeatureTypeStyle><Rule>` +
    `<RasterSymbolizer><Opacity>${opacity}</Opacity>` +
    `<ColorMap type="ramp">${colorMapEntries}</ColorMap>` +
    `</RasterSymbolizer></Rule></FeatureTypeStyle></UserStyle></NamedLayer></StyledLayerDescriptor>`
  );
}

export function filterContourLevels(
  levels: number[],
  minVal?: number,
  maxVal?: number
): number[] {
  return levels.filter((level) => {
    if (minVal !== undefined && level < minVal) return false;
    if (maxVal !== undefined && level > maxVal) return false;
    return true;
  });
}

function xmlText(value: string | number): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function buildContourSldBody({
  workspace,
  layerName,
  levels,
  stroke = "#3F3F3F",
  strokeWidth = 1,
  strokeOpacity = 0.85,
}: {
  workspace: string;
  layerName: string;
  levels: number[];
  stroke?: string;
  strokeWidth?: number;
  strokeOpacity?: number;
}): string {
  const levelEntries = levels
    .map((level) => `<ogc:Literal>${xmlText(level)}</ogc:Literal>`)
    .join("");

  return (
    `<StyledLayerDescriptor version="1.0.0" ` +
    `xmlns="http://www.opengis.net/sld" ` +
    `xmlns:ogc="http://www.opengis.net/ogc" ` +
    `xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">` +
    `<NamedLayer><Name>${xmlText(workspace)}:${xmlText(layerName)}</Name>` +
    `<UserStyle><Title>Filtered Contour</Title><FeatureTypeStyle>` +
    `<Transformation><ogc:Function name="ras:Contour">` +
    `<ogc:Function name="parameter"><ogc:Literal>data</ogc:Literal></ogc:Function>` +
    `<ogc:Function name="parameter"><ogc:Literal>levels</ogc:Literal>${levelEntries}</ogc:Function>` +
    `</ogc:Function></Transformation>` +
    `<Rule><Name>filtered-contours</Name><LineSymbolizer><Stroke>` +
    `<CssParameter name="stroke">${xmlText(stroke)}</CssParameter>` +
    `<CssParameter name="stroke-width">${xmlText(strokeWidth)}</CssParameter>` +
    `<CssParameter name="stroke-opacity">${xmlText(strokeOpacity)}</CssParameter>` +
    `</Stroke></LineSymbolizer><TextSymbolizer>` +
    `<Label><ogc:PropertyName>value</ogc:PropertyName></Label>` +
    `<Font><CssParameter name="font-family">Arial</CssParameter>` +
    `<CssParameter name="font-style">Normal</CssParameter>` +
    `<CssParameter name="font-size">10</CssParameter></Font>` +
    `<LabelPlacement><LinePlacement /></LabelPlacement>` +
    `<Halo><Radius><ogc:Literal>2</ogc:Literal></Radius><Fill>` +
    `<CssParameter name="fill">#FFFFFF</CssParameter>` +
    `<CssParameter name="fill-opacity">0.6</CssParameter>` +
    `</Fill></Halo><Fill>` +
    `<CssParameter name="fill">#303030</CssParameter>` +
    `</Fill><Priority>2000</Priority>` +
    `<VendorOption name="followLine">true</VendorOption>` +
    `<VendorOption name="repeat">200</VendorOption>` +
    `<VendorOption name="maxDisplacement">50</VendorOption>` +
    `<VendorOption name="maxAngleDelta">45</VendorOption>` +
    `</TextSymbolizer></Rule></FeatureTypeStyle></UserStyle></NamedLayer></StyledLayerDescriptor>`
  );
}
